import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { type ReactElement } from 'react';

import { colorOpennessAtom } from '../state/draftAtoms';

const COLORS = [
  { key: 'W' as const, label: 'White', bg: 'bg-amber-100', text: 'text-amber-900' },
  { key: 'U' as const, label: 'Blue', bg: 'bg-blue-400', text: 'text-blue-900' },
  { key: 'B' as const, label: 'Black', bg: 'bg-zinc-600', text: 'text-zinc-100' },
  { key: 'R' as const, label: 'Red', bg: 'bg-red-500', text: 'text-red-100' },
  { key: 'G' as const, label: 'Green', bg: 'bg-green-500', text: 'text-green-100' },
] as const;

function getSignalLabel(openness: number): string {
  if (openness >= 0.65) return 'Open';
  if (openness >= 0.4) return 'Contested';
  return 'Cut';
}

function getSignalColor(openness: number): string {
  if (openness >= 0.65) return 'text-green-400';
  if (openness >= 0.4) return 'text-yellow-400';
  return 'text-red-400';
}

export default function ColorSignals(): ReactElement {
  const openness = useAtomValue(colorOpennessAtom);

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold tracking-wider text-zinc-400 uppercase">Color Signals</h3>
      <div className="space-y-1.5">
        {COLORS.map(({ key }) => {
          const value = openness[key];
          const signalLabel = getSignalLabel(value);
          const signalColor = getSignalColor(value);
          return (
            <div key={key} className="flex items-center gap-2">
              <span className="w-4 text-center text-xs font-bold text-zinc-300">{key}</span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-zinc-800">
                <div
                  className={clsx(
                    'h-full rounded-full transition-all duration-500',
                    value >= 0.65 && 'bg-green-500/70',
                    value >= 0.4 && value < 0.65 && 'bg-yellow-500/70',
                    value < 0.4 && 'bg-red-500/70',
                  )}
                  style={{ width: `${value * 100}%` }}
                />
              </div>
              <span className={clsx('w-16 text-right text-[10px] font-medium', signalColor)}>{signalLabel}</span>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-zinc-600">Based on what AI opponents are not drafting</p>
    </div>
  );
}
